import { useId, useState, type ReactNode } from 'react'

const bubble =
  'pointer-events-none absolute top-full left-1/2 z-10 -translate-x-1/2 rounded-lg bg-neutral-900 px-3 py-2 text-left text-xs font-normal text-white shadow-lg transition dark:bg-neutral-100 dark:text-neutral-900'

/**
 * Infobulle au survol et au focus : le declencheur est un bouton, pour
 * qu'elle s'ouvre aussi au clavier et au doigt, ce qu'un attribut `title`
 * ne fait pas.
 */
export default function Tooltip({ children, content }: { children: ReactNode; content: ReactNode }) {
  const id = useId()

  return (
    <span className="group relative inline-block">
      <button
        type="button"
        aria-describedby={id}
        className="cursor-help rounded underline decoration-neutral-400 decoration-dotted underline-offset-4 outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/20 dark:focus-visible:ring-neutral-100/30"
      >
        {children}
      </button>
      <span
        id={id}
        role="tooltip"
        className={`${bubble} invisible mt-1.5 w-64 max-w-[80vw] opacity-0 group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100`}
      >
        {content}
      </span>
    </span>
  )
}

/**
 * Petite icone d'information et son infobulle. La bulle se centre sur le plus
 * proche parent `relative`, a donner a la ligne entiere et non a l'icone :
 * centree sur une icone proche du bord, elle sortirait de la fenetre.
 *
 * Un toucher l'ouvre et la referme : sur un telephone il n'y a ni survol ni focus.
 */
export function InfoTooltip({ label, content }: { label: string; content: ReactNode }) {
  const id = useId()
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        aria-label={label}
        aria-describedby={id}
        aria-expanded={open}
        onClick={() => setOpen((previous) => !previous)}
        onBlur={() => setOpen(false)}
        className="peer flex h-5 w-5 shrink-0 cursor-help items-center justify-center rounded-full text-neutral-500 outline-none hover:text-neutral-900 focus-visible:ring-2 focus-visible:ring-neutral-900/20 dark:text-neutral-400 dark:hover:text-neutral-100 dark:focus-visible:ring-neutral-100/30"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 11v6M12 7.5v.01" />
        </svg>
      </button>
      <span
        id={id}
        role="tooltip"
        className={`${bubble} mt-0.5 w-56 max-w-full peer-hover:visible peer-hover:opacity-100 peer-focus-visible:visible peer-focus-visible:opacity-100 ${
          open ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
      >
        {content}
      </span>
    </>
  )
}
